import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPendingAction, clearPendingAction, PendingAction } from '@/utils/pendingActionStorage';

/**
 * Hook to handle pending actions after authentication
 * 
 * Usage:
 * const { processPendingAction } = usePendingActionHandler();
 * 
 * // After successful auth:
 * const action = processPendingAction();
 * if (!action) {
 *   // No pending action, navigate to default
 *   navigate('/workspace/talent/jobs');
 * }
 */
export function usePendingActionHandler() {
  const navigate = useNavigate();

  /**
   * Process any pending action after authentication
   * Navigates to the return path if action exists
   * 
   * @returns The pending action if one was processed, null otherwise
   */
  const processPendingAction = useCallback((): PendingAction | null => {
    const pendingAction = getPendingAction();
    
    if (!pendingAction) {
      console.log('[PendingAction] No pending action found');
      return null;
    }
    
    console.log('[PendingAction] Processing:', pendingAction);
    
    // Clear before navigating to prevent double execution
    clearPendingAction();
    
    // Navigate to the stored return path
    // The return path contains all the query params needed to trigger the action
    navigate(pendingAction.returnPath, { replace: true });
    
    return pendingAction;
  }, [navigate]);

  /**
   * Check if there's a pending action without processing it
   */
  const hasPendingAction = useCallback((): boolean => {
    return getPendingAction() !== null;
  }, []);

  return { 
    processPendingAction,
    hasPendingAction
  };
}
